import { useQuery } from '@tanstack/react-query';
import { productsApi } from '../../products/api/productsApi';
import { ApiFetch } from '../../../shared/utils/fetch';
import { unwrap, type Enveloped } from '../../../shared/utils/unwrap';

export interface TaggableVariantOption {
  value: string;
  label: string;
  sublabel?: string;
}

/** The fields osprey-api's `GET /products/:productId/variants` (product-service direct) returns. */
interface ProductServiceVariant {
  id: string;
  sku?: string;
  attributes?: Record<string, string>;
}

const variantLabel = (variant: ProductServiceVariant): string => {
  if (variant.sku) return variant.sku;
  const attrs = variant.attributes ? Object.values(variant.attributes).filter(Boolean) : [];
  return attrs.length > 0 ? attrs.join(' · ') : variant.id;
};

/**
 * Flattens the current tenant's product catalogue into checkbox-picker options for the
 * atelier/rack-and-box portfolio tagging picker (charter tags booking-service SERVICES instead
 * — see useCharterServices). There is no flat "all variants for a tenant" endpoint today, so
 * this lists products (`GET /products`, tenant-scoped via ApiFetch's x-tenant-id header) and
 * fans out to `GET /products/:id/variants` (osprey-api proxies this straight to product-service,
 * so it works for any tenant regardless of solution) per product. Fine for the modest
 * catalogues these back-offices manage today; a dedicated flat "variants by tenant" endpoint
 * would be needed to scale beyond a page of products.
 */
const fetchTaggableVariants = async (): Promise<TaggableVariantOption[]> => {
  const products = await productsApi.getAll({ 'page[size]': 100 });
  const perProduct = await Promise.all(
    products.data.map(async (product) => {
      try {
        const res = await ApiFetch.get<Enveloped<ProductServiceVariant[]>>(`/products/${product.id}/variants`);
        return unwrap(res).map((variant) => ({
          value: variant.id,
          label: variantLabel(variant),
          sublabel: product.name,
        }));
      } catch {
        // A single product's variants failing to load shouldn't blank the whole picker.
        return [];
      }
    }),
  );
  return perProduct.flat();
};

export const useTaggableVariants = (tenantId: string | null | undefined, enabled: boolean) =>
  useQuery({
    queryKey: ['portfolio', 'taggable-variants', tenantId],
    queryFn: fetchTaggableVariants,
    enabled: enabled && !!tenantId,
    staleTime: 60 * 1000,
  });
